import { useGeneralSettingsFormData } from "@/api/cms";
import {
  createCatalogFlowTemplate,
  getCatalogFlow,
  listPuckPages,
  upsertCatalogFlow,
  type CatalogFlowStep,
  type CatalogFlowTarget,
} from "@/api/cms-puck/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { cmsNew, useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";

const FLOW_KEY = ["cms-puck", "catalog-flow"] as const;
const PAGES_KEY = ["cms-puck", "pages"] as const;
const NONE_VALUE = "__none__";

/** Étapes techniques du parcours catalogue.
 * Clés + fallbacks — ne pas résoudre cmsNew() au niveau module (bundle i18n async). */
const STEPS: { step: CatalogFlowStep; labelKey: CmsNewKey; fallback: string }[] =
  [
    {
      step: "DECOUPAGES",
      labelKey: "cms.new.auto.label.decoupage_tous_les_decoupages",
      fallback: "Découpage (tous les découpages)",
    },
    {
      step: "LISTING",
      labelKey: "cms.new.auto.label.listing_dun_decoupage",
      fallback: "Listing d'un découpage",
    },
    {
      step: "DETAIL",
      labelKey: "cms.new.auto.label.fiche_uvre_multiple",
      fallback: "Fiche œuvre / multiple",
    },
  ];

const TARGETS: {
  target: CatalogFlowTarget;
  labelKey: CmsNewKey;
  fallback: string;
}[] = [
  {
    target: "ARTWORKS",
    labelKey: "cms.new.auto.label.uvres",
    fallback: "Œuvres",
  },
  {
    target: "MULTIPRINTS",
    labelKey: "cms.new.auto.label.multiples",
    fallback: "Multiples",
  },
];

function catalogRoleBadgeLabel(
  target: CatalogFlowTarget,
  step: CatalogFlowStep,
): string {
  const t =
    target === "ARTWORKS"
      ? cmsNew("cms.new.catalogue.badgeArtworks", "œuvres")
      : cmsNew("cms.new.catalogue.badgeMultiples", "multiples");
  const s =
    step === "LISTING"
      ? cmsNew("cms.new.catalogue.stepListing", "Listing")
      : step === "DETAIL"
        ? cmsNew("cms.new.catalogue.stepDetail", "Fiche")
        : cmsNew("cms.new.catalogue.stepPresentation", "Découpage");
  return `${s} ${t}`;
}

type SelectablePage = {
  id: string;
  name: string;
  status: string;
  isCatalogTemplate: boolean;
};

function FlowCell({
  target,
  step,
  value,
  pages,
  onAssign,
  onCreateTemplate,
  saving,
  creating,
}: {
  target: CatalogFlowTarget;
  step: CatalogFlowStep;
  value: string | null;
  pages: SelectablePage[];
  onAssign: (pageId: string | null) => void;
  onCreateTemplate: () => void;
  saving: boolean;
  creating: boolean;
}) {
  const cmsNew = useCmsNew();
  const navigate = useNavigate();
  const assigned = pages.find((p) => p.id === value);
  const isDraft = assigned && assigned.status !== "PUBLISHED";
  const templatePages = pages.filter((p) => p.isCatalogTemplate);
  const classicPages = pages.filter((p) => !p.isCatalogTemplate);

  const pageLabel = (page: SelectablePage) =>
    `${page.name}${
      page.status !== "PUBLISHED"
        ? ` (${puckPageStatusLabel(cmsNew, page.status)})`
        : ""
    }`;

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <Label className="text-sm font-medium text-slate-800">
        {catalogRoleBadgeLabel(target, step)}
      </Label>
      <Select
        value={value ?? NONE_VALUE}
        onValueChange={(next) =>
          onAssign(next === NONE_VALUE ? null : next)
        }
        disabled={saving || creating}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={cmsNew(
              "cms.new.auto.placeholder.aucune_page_systeme",
              "Aucune (pages système)",
            )}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>
            {cmsNew(
              "cms.new.auto.selectItem.aucune_page_systeme",
              "Aucune (pages système)",
            )}
          </SelectItem>
          {templatePages.length > 0 ? (
            <SelectItem value="__hdr_templates__" disabled>
              {cmsNew(
                "cms.new.catalogue.selectGroup.templates",
                "— Templates catalogue —",
              )}
            </SelectItem>
          ) : null}
          {templatePages.map((page) => (
            <SelectItem key={page.id} value={page.id}>
              {pageLabel(page)}
            </SelectItem>
          ))}
          {classicPages.length > 0 ? (
            <SelectItem value="__hdr_classic__" disabled>
              {cmsNew(
                "cms.new.catalogue.selectGroup.classic",
                "— Pages classiques —",
              )}
            </SelectItem>
          ) : null}
          {classicPages.map((page) => (
            <SelectItem key={page.id} value={page.id}>
              {pageLabel(page)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isDraft ? (
        <p className="text-xs text-amber-700">
          {cmsPhrase(cmsNew, "draftPublishHint")}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        {value ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            onClick={() => navigate(`/cms/advanced/${value}`)}
          >
            <ExternalLink size={14} />
            {assigned && !assigned.isCatalogTemplate
              ? cmsNew("cms.new.auto.action.editer_la_page", "Éditer la page")
              : cmsPhrase(cmsNew, "editTemplate")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1"
            disabled={creating}
            onClick={onCreateTemplate}
          >
            <Plus size={14} />
            {creating
              ? cmsPhrase(cmsNew, "creating")
              : cmsPhrase(cmsNew, "createTemplate")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CMSAdvancedCatalogue() {
  const cmsNew = useCmsNew();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: generalSettings } = useGeneralSettingsFormData();
  const isMixedCRMode = generalSettings?.isMixedCRMode ?? false;

  const flowQuery = useQuery({ queryKey: FLOW_KEY, queryFn: getCatalogFlow });
  const pagesQuery = useQuery({ queryKey: PAGES_KEY, queryFn: () => listPuckPages() });

  const saveMutation = useMutation({
    mutationFn: upsertCatalogFlow,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.parcours_catalogue_enregistre", "Parcours catalogue enregistré"),
        variant: "success",
        duration: 1600,
      });
    },
    onError: () => {
      toast({
        title: cmsNew("cms.new.auto.title.impossible_denregistrer_la_liaison", "Impossible d'enregistrer la liaison"),
        variant: "error",
      });
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: createCatalogFlowTemplate,
    onSuccess: ({ page }) => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.template_catalogue_cree", "Template catalogue créé"),
        variant: "success",
        duration: 1600,
      });
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : cmsPhrase(cmsNew, "createTemplateFailed");
      toast({
        title: message,
        variant: "error",
      });
    },
  });

  const bindings = flowQuery.data ?? [];
  const pages = pagesQuery.data ?? [];
  /** Templates catalogue + pages éditoriales classiques (hors shell / maintenance). */
  const selectablePages: SelectablePage[] = pages
    .filter(
      (p) =>
        !p.isMaintenancePage &&
        !p.isAbonnementPage &&
        !(p.shellFlowRoles?.length ?? 0),
    )
    .map((p) => ({
      id: p.id,
      name: p.name,
      status: p.status,
      isCatalogTemplate: (p.catalogFlowRoles?.length ?? 0) > 0,
    }))
    .sort((a, b) => {
      if (a.isCatalogTemplate !== b.isCatalogTemplate) {
        return a.isCatalogTemplate ? -1 : 1;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

  const pageIdFor = (target: CatalogFlowTarget, step: CatalogFlowStep) =>
    bindings.find((b) => b.target === target && b.step === step)?.pageId ?? null;

  const visibleTargets = isMixedCRMode
    ? TARGETS.filter((t) => t.target === "ARTWORKS")
    : TARGETS;

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.parcours_catalogue", "Parcours catalogue")}
      subtitle={cmsNew("cms.new.auto.subtitle.templates_techniques_listing_et_fiche_les_decoupages_se_placent_librement_via_le", "Templates techniques listing et fiche. Les découpages se placent librement via le bloc « Découpage » sur vos pages.")}
    >
      <div className="space-y-6 px-6 py-4">
        <p className="text-sm text-slate-600">{cmsPhrase(cmsNew, "catalogFlowIntro")}</p>
        <p className="text-sm text-slate-600">
          {cmsNew(
            "cms.new.catalogFlow.urlPattern",
            "URLs : /{langue}/{slug-présentation} → découpages, puis /{slug-découpage} → listing, puis /{id} → fiche. Le slug de présentation est celui de la page liée ci-dessous (étape Découpage).",
          )}
        </p>

        {flowQuery.isLoading || pagesQuery.isLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${visibleTargets.length}, minmax(0, 1fr))`,
            }}
          >
            {visibleTargets.map(({ target, labelKey, fallback }) => (
              <div key={target} className="space-y-3">
                <h2 className="text-base font-semibold text-slate-900">
                  {cmsNew(labelKey, fallback)}
                </h2>
                {STEPS.map(({ step, labelKey: stepKey, fallback: stepFallback }) => (
                  <div key={step}>
                    <p className="mb-1 text-xs text-slate-500">
                      {cmsNew(stepKey, stepFallback)}
                    </p>
                    <FlowCell
                      target={target}
                      step={step}
                      value={pageIdFor(target, step)}
                      pages={selectablePages}
                      saving={saveMutation.isPending}
                      creating={createTemplateMutation.isPending}
                      onAssign={(pageId) =>
                        saveMutation.mutate({ target, step, pageId })
                      }
                      onCreateTemplate={() =>
                        createTemplateMutation.mutate({ target, step })
                      }
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
          <p className="font-medium text-slate-800">{cmsNew("cms.new.auto.text.blocs_sur_les_pages_editoriales", "Blocs sur les pages éditoriales")}</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>
              <strong>{cmsNew("cms.new.auto.text.decoupage", "Découpage")}</strong> — {cmsNew("cms.new.catalogue.decoupageBlockDesc", "une carte par découpage, placée où vous voulez")}
            </li>
            <li>
              <strong>{cmsNew("cms.new.auto.text.filtres_catalogue", "Filtres catalogue")}</strong> — {cmsNew("cms.new.catalogue.filtersBlockDesc", "barre de recherche optionnelle (redirige vers /search)")}
            </li>
          </ul>
          <p className="mt-2">
            {cmsNew("cms.new.catalogue.technicalTemplatesHint", "Templates techniques (listing / fiche) : créés ici, absents de la liste des pages.")}
          </p>
          <p className="mt-2">
            {cmsNew("cms.new.catalogue.manageDecoupagesText", "Gérez les découpages dans")}{" "}
            <Link
              to="/cms/advanced/decoupages"
              className="font-medium text-slate-800 underline"
            >
              {cmsNew("cms.new.catalogue.decoupagesLink", "Découpages")}
            </Link>
            .
          </p>
        </div>
      </div>
    </CmsAdminShell>
  );
}

/** Libellé badge liste pages (exporté pour advanced.tsx). */
export { catalogRoleBadgeLabel };
